import { DayObject, Timestamp } from "@/types/date"

import { PagingMeta } from "@/lib/paging"
import { baseApi } from "./base-api"

export interface LoyaltyHumanReview {
  decision: "keep" | "reject" | "unresolved" | "reviewed"
  note: string
  source_urls: string[]
  reviewer: string
  reviewed_at: Timestamp
  complete?: boolean
  revision?: string
}

export interface LoyaltyIssueRule {
  id: number
  program_name: string
  replaced_rule_id?: number
  rule_key: string
  statement: string
  version: number
  review_status: string
  sources: { url: string; quote: string; captured_at: Timestamp }[]
  human_review: LoyaltyHumanReview | null
}

export interface LoyaltyLookupSummary {
  state: "not_called" | "empty" | "returned" | "failed" | "unavailable"
  rule_count: number | null
  call_count: number | null
  error_count: number | null
}

export interface LoyaltyLookupResults {
  summary: LoyaltyLookupSummary
  lookup_results:
    | {
        event_id: number
        request: Record<string, unknown> | null
        status: "success" | "error"
        rule_ids: number[]
        result: string
      }[]
    | null
}

export interface LoyaltyDataIssue {
  id: number
  reported_on: DayObject
  signal: string
  category: string
  program: { id: number; key: string; program_name: string } | null
  facet: string | null
  needed: string
  // What the traveler wrote, from their chat; agent_request is the main bot's brief to the loyalty agent.
  user_message: string
  agent_request: string | null
  evidence: Record<string, unknown>
  recommended_action: string | null
  automatic_action: string | null
  agent_session_id: number
  loyalty_gap_id: number | null
  created_at: Timestamp
  review: LoyaltyHumanReview | null
  review_complete?: boolean
  rules: LoyaltyIssueRule[]
  unavailable_rule_ids?: number[]
  candidate_rules?: LoyaltyIssueRule[]
  available_rules?: LoyaltyIssueRule[]
  lookup_summary?: LoyaltyLookupSummary | null
  sources: { url: string; quote: string | null }[]
}

export interface LoyaltyDataIssueDay {
  day: DayObject
  counts: Record<string, number>
}

export interface LoyaltyDataIssuesResponse {
  data: LoyaltyDataIssue[]
  meta: PagingMeta
  days: LoyaltyDataIssueDay[]
  categories: string[]
  // Every category's count for the day chosen, whichever category is chosen.
  category_counts: Record<string, number>
  // Distinct traveler messages behind the issues listed, across every page.
  message_count: number
}

export const loyaltyDataIssuesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLoyaltyIssueLookups: builder.query<LoyaltyLookupResults, number>({
      query: (id) => ({
        url: `/api/store-admin/loyalty_data_issues/${id}/lookups`,
      }),
    }),
    getLoyaltyIssueRule: builder.query<
      { record: Record<string, unknown> },
      { issue_id: number; rule_id: number }
    >({
      query: ({ issue_id, rule_id }) => ({
        url: `/api/store-admin/loyalty_data_issues/${issue_id}/rules/${rule_id}`,
      }),
    }),
    getLoyaltyDataIssues: builder.query<
      LoyaltyDataIssuesResponse,
      {
        day: string | null
        category: string | null
        page: number
        review_status?: string
      }
    >({
      providesTags: ["LoyaltyDataIssues"],
      query: ({ day, category, page, review_status }) => ({
        url: "/api/store-admin/loyalty_data_issues",
        params: {
          day: day ?? undefined,
          category: category ?? undefined,
          page,
          review_status,
        },
      }),
    }),
    reviewLoyaltyDataIssue: builder.mutation<
      LoyaltyDataIssue,
      {
        id: number
        decision: LoyaltyHumanReview["decision"]
        rule_id?: number
        note: string
        source_urls: string[]
        expected_revision?: string | null
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/api/store-admin/loyalty_data_issues/${id}`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: ["LoyaltyDataIssues"],
      async onQueryStarted(
        _,
        { dispatch, getState, queryFulfilled }
      ): Promise<void> {
        // A rejected mutation leaves the cache unchanged; the form displays the server error.
        const result = await queryFulfilled.catch(() => null)
        if (result === null) return
        const updated = result.data
        const cached = loyaltyDataIssuesApi.util.selectCachedArgsForQuery(
          getState(),
          "getLoyaltyDataIssues"
        )
        for (const args of cached) {
          dispatch(
            loyaltyDataIssuesApi.util.updateQueryData(
              "getLoyaltyDataIssues",
              args,
              (draft) => {
                const index = draft.data.findIndex(
                  (issue) => issue.id === updated.id
                )
                if (index !== -1) draft.data[index] = updated
              }
            )
          )
        }
      },
    }),
  }),
})

export const {
  useGetLoyaltyIssueLookupsQuery,
  useGetLoyaltyIssueRuleQuery,
  useGetLoyaltyDataIssuesQuery,
  useReviewLoyaltyDataIssueMutation,
} = loyaltyDataIssuesApi
